import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Switch, Space, message, Upload, Card, Image, Slider } from 'antd';
import { PlusOutlined, EditOutlined, UploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { PostAvatar } from '../../../dao/jutge/GetJavamonConfig';
import { getBase64, getImageUrl, javamonImage } from './javamonUtils';
import { RewardsUsageView } from './RewardsUsageView';

interface TabAvatarsProps {
    isAdmin: boolean;
    loading: boolean;
    avatars: any[];
    fetchSkinsConfig: () => Promise<void>;
    rewardsUsage: any;
    loadingRewardsUsage: boolean;
    fetchRewardsUsage: () => Promise<void>;
}

export const TabAvatars: React.FC<TabAvatarsProps> = ({
    isAdmin,
    loading,
    avatars,
    fetchSkinsConfig,
    rewardsUsage,
    loadingRewardsUsage,
    fetchRewardsUsage
}) => {
    const [view, setView] = useState<'table' | 'grid' | 'usage'>('table');
    const [cardSize, setCardSize] = useState<number>(64);
    const [avatarModal, setAvatarModal] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [fileList, setFileList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [form] = Form.useForm();

    const handleToggleActive = async (record: any, checked: boolean) => {
        try {
            const formData = new FormData();
            const body: any = {
                ...record,
                active: checked ? 1 : 0
            };
            delete body.imagen;
            formData.append('json', JSON.stringify(body));

            const res = await PostAvatar(formData);
            if (!res) throw new Error('Error');
            message.success("Estado actualizado");
            fetchSkinsConfig();
        } catch (e) {
            message.error("Error al actualizar estado");
        }
    };

    const handleSaveAvatar = async (values: any) => {
        try {
            const formData = new FormData();
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('file', fileList[0].originFileObj);
            }

            const body: any = {
                ...values,
                id: editingItem?.id || 0,
                active: values.active ? 1 : 0
            };

            if (fileList.length > 0 && fileList[0].originFileObj) {
                const b64 = await getBase64(fileList[0].originFileObj);
                body.imagen = b64;
            }

            formData.append('json', JSON.stringify(body));

            const res = await PostAvatar(formData);
            if (!res) throw new Error('Error');
            message.success("Avatar guardado correctamente");
            setAvatarModal(false);
            setFileList([]);
            setPreviewImage(null);
            fetchSkinsConfig();
        } catch (e) {
            message.error("Error al guardar avatar");
        }
    };

    const openAvatarModal = (record: any = null) => {
        setEditingItem(record);
        if (record) {
            form.setFieldsValue({
                ...record,
                active: record.active === undefined ? true : record.active === 1
            });
        } else {
            form.resetFields();
        }
        setFileList([]);
        setPreviewImage(null);
        setAvatarModal(true);
    };

    const columns = [
        {
            title: 'Imagen',
            dataIndex: 'imagen',
            key: 'imagen',
            render: (_: any, record: any) => <Image src={javamonImage(record)} alt="img" width={50} />
        },
        { title: 'Nombre', dataIndex: 'nom', key: 'nom' },
        {
            title: 'Activo',
            dataIndex: 'active',
            key: 'active',
            render: (val: number, record: any) => (
                isAdmin ? (
                    <Switch
                        checked={val === 1}
                        onChange={(checked) => handleToggleActive(record, checked)}
                    />
                ) : (
                    <Tag color={val ? 'green' : 'red'}>{val ? 'SÍ' : 'NO'}</Tag>
                )
            )
        },
        ...(isAdmin ? [{
            title: 'Acciones',
            key: 'actions',
            render: (_: any, record: any) => (
                <Button icon={<EditOutlined />} onClick={() => openAvatarModal(record)} />
            )
        }] : [])
    ];

    const renderGrid = () => {
        return (
            <Card size="small" title={`${avatars.length} avatares`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, maxWidth: 320 }}>
                    <span style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>Tamaño:</span>
                    <Slider
                        min={40}
                        max={160}
                        step={4}
                        value={cardSize}
                        onChange={(v) => setCardSize(v as number)}
                        style={{ flex: 1 }}
                    />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {avatars.length === 0 ? (
                        <span style={{ color: '#aaa' }}>No hay avatares.</span>
                    ) : (
                        avatars.map((it: any) => {
                            const cardWidth = cardSize + 36;
                            return (
                                <div
                                    key={it.id}
                                    style={{
                                        width: cardWidth,
                                        padding: 6,
                                        borderRadius: 10,
                                        border: '2px solid #e0e0e0',
                                        background: '#fff',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                        opacity: it.active ? 1 : 0.55,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 4
                                    }}
                                >
                                    <div
                                        onClick={() => { if (isAdmin) openAvatarModal(it); }}
                                        title={isAdmin ? `Editar ${it.nom}` : it.nom}
                                        style={{ cursor: isAdmin ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <img src={javamonImage(it)} alt={it.nom} width={cardSize} height={cardSize} style={{ objectFit: 'contain' }} />
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', lineHeight: 1.1, wordBreak: 'break-word' }}>
                                        {it.nom}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </Card>
        );
    };

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                {isAdmin && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openAvatarModal()}>
                        Nuevo Avatar
                    </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={fetchSkinsConfig} loading={loading}>
                    Refrescar
                </Button>
            </Space>

            <div style={{ marginBottom: 12 }}>
                <Select
                    value={view}
                    onChange={(v) => setView(v)}
                    style={{ width: 240 }}
                    options={[
                        { value: 'table', label: '📋 Vista Tabla' },
                        { value: 'grid', label: '🖼️ Vista Cuadrícula' },
                        { value: 'usage', label: '🎁 Vista de Uso / Recompensas' }
                    ]}
                />
            </div>

            {view === 'table' && <Table dataSource={avatars} columns={columns} loading={loading} rowKey="id" />}
            {view === 'grid' && renderGrid()}
            {view === 'usage' && (
                <RewardsUsageView
                    type="avatar"
                    items={avatars}
                    rewardsUsage={rewardsUsage}
                    loading={loading}
                    loadingRewardsUsage={loadingRewardsUsage}
                    fetchRewardsUsage={fetchRewardsUsage}
                />
            )}

            {/* Modal Avatar */}
            <Modal
                title={editingItem ? "Editar Avatar" : "Crear Avatar"}
                open={avatarModal}
                onCancel={() => setAvatarModal(false)}
                onOk={() => form.submit()}
                destroyOnClose
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSaveAvatar}
                    initialValues={{ active: true }}
                >
                    <Form.Item name="nom" label="Nombre" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item label="Subir imagen/icono">
                        <Upload
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={async ({ fileList }) => {
                                setFileList(fileList);
                                if (fileList.length > 0 && fileList[0].originFileObj) {
                                    const b64 = await getBase64(fileList[0].originFileObj);
                                    setPreviewImage(b64);
                                } else {
                                    setPreviewImage(null);
                                }
                            }}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
                        </Upload>
                        {(previewImage || editingItem) && (
                            <div style={{ marginTop: 10 }}>
                                <Image
                                    src={previewImage || getImageUrl(editingItem, 'avatar')}
                                    alt="preview"
                                    width={100}
                                    style={{ borderRadius: 8, border: '1px solid #d9d9d9', padding: 4 }}
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item name="active" label="Activo" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
